import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../components/layout';
import { useAdminNavigation } from '../hooks/useAdminNavigation';
import { adminCreditSettings, adminSaveCreditSettings, refusalMessage, type CreditSettings } from '../../../api/creditsApi';
import { S } from './adminPageStyles';

/** Admin › Credits: the rate ($1 = N credits), the custom minimum and the preset purchase amounts.
 *  Changes apply to the next purchase; a purchase already made keeps the credits it was sold for. */
const AdminCreditsPage: React.FC = () => {
  const { sidebarCollapsed, setSidebarCollapsed, activeSection, setSelectedRegulation, handleNavClick, handleLogout } = useAdminNavigation();
  const [saved, setSaved] = useState<CreditSettings | null>(null);
  const [rate, setRate] = useState('');
  const [customMin, setCustomMin] = useState('');
  const [presets, setPresets] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const showing = (s: CreditSettings) => {
    setSaved(s);
    setRate(String(s.rate_credits_per_usd));
    setCustomMin(String(s.custom_min_usd));
    setPresets(s.presets_usd.join(', '));
  };

  useEffect(() => {
    adminCreditSettings().then(showing).catch((e) => setError(refusalMessage(e, 'Failed to load the credit settings')));
  }, []);

  const save = async () => {
    setError(null);
    setNotice(null);
    const presetList = presets.split(',').map((x) => x.trim()).filter(Boolean).map(Number);
    if (presetList.some((n) => !Number.isInteger(n))) {
      setError('Preset amounts must be whole dollars, separated by commas');
      return;
    }
    setSaving(true);
    try {
      const next = await adminSaveCreditSettings({
        rate_credits_per_usd: Number(rate),
        custom_min_usd: Number(customMin),
        presets_usd: presetList,
      });
      showing(next);
      setNotice('Saved. New purchases use these settings.');
    } catch (e) {
      setError(refusalMessage(e, 'The settings could not be saved'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout collapsed={sidebarCollapsed} activeSection={activeSection} welcomeLabel="Credits" selectedRegulation={null}
      onToggleSidebar={() => setSidebarCollapsed((p) => !p)} onNavClick={handleNavClick} onProfileClick={() => handleNavClick('profile')}
      onLogout={handleLogout} onRegulationModalClose={() => setSelectedRegulation(null)}>
      <section style={S.section}>
        <h2 style={S.h2}>AI credits</h2>
        <p style={S.sub}>
          How customers buy AI credits. The rate is the number of credits one US dollar buys. Customers can buy a preset
          amount, or a custom amount at or above the custom minimum. Nothing below $20 can be bought.
        </p>
        {error && <div style={S.error} role="alert">{error}</div>}
        {notice && <div style={S.ok} role="status">{notice}</div>}
        <div style={{ display: 'grid', gap: 14, maxWidth: 520 }}>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={S.muted}>Credits per US dollar</span>
            <input style={S.input} type="number" min={1} step={1} aria-label="Credits per US dollar" value={rate}
              onChange={(e) => setRate(e.target.value)} />
          </label>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={S.muted}>Custom amount minimum (US dollars)</span>
            <input style={S.input} type="number" min={20} step={1} aria-label="Custom amount minimum" value={customMin}
              onChange={(e) => setCustomMin(e.target.value)} />
          </label>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={S.muted}>Preset amounts (US dollars, separated by commas)</span>
            <input style={S.input} type="text" aria-label="Preset amounts" value={presets}
              onChange={(e) => setPresets(e.target.value)} />
          </label>
          <div>
            <button style={S.primary} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save credit settings'}</button>
          </div>
        </div>
        {saved && (
          <p style={{ ...S.muted, marginTop: 16 }}>
            Currently: $1 buys {saved.rate_credits_per_usd.toLocaleString('en-US')} credits. Presets: {saved.presets_usd.map((p) => `$${p}`).join(', ')}.
            Custom amounts from ${saved.custom_min_usd.toLocaleString('en-US')}.
          </p>
        )}
      </section>
    </DashboardLayout>
  );
};

export default AdminCreditsPage;
